import React, { useCallback, useContext, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import { Box, CircularProgress } from "@mui/material";
import { HomeAppContext } from "../Home";
import api from "../../Services/api";
import { errorManagement } from "../../Services/errorManagement";
import Form, { ClientDesignFormMode } from "./Form";
import GenericDialog from "../GenericDialog";
import { ACTION_TYPES, StyleGuideProvider, useStyleGuide } from "./context/StyleGuideContext";

const mapSelectionIds = (items = []) =>
  (items || [])
    .map((item) => {
      const value = item?.id ?? item?.Id ?? item;
      const numericValue = Number(value);
      return Number.isNaN(numericValue) ? value : numericValue;
    })
    .filter((value) => value !== undefined && value !== null);

const mapMetadataPayload = (metadata = {}) => {
  const result = {};
  Object.entries(metadata || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      result[key] = mapSelectionIds(value);
    } else {
      result[key] = value;
    }
  });
  return result;
};

const Edit = (props) => (
  <StyleGuideProvider>
    <EditImplementation {...props} />
  </StyleGuideProvider>
);

function EditImplementation({ open, setOpen, entry, onEdit }) {
  const { homeDispatch } = useContext(HomeAppContext);
  const { state, dispatch } = useStyleGuide();
  const { entity } = state;

  const [loading, setLoading] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState([]);
  const [designNeedsReviewed, setDesignNeedsReviewed] = useState(false);
  const [tagsFromApi, setTagsFromApi] = useState([]);
  const [familiesFromApi, setFamiliesFromApi] = useState([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);

  useEffect(() => {
    if (entry !== null && open) {
      dispatch({
        type: ACTION_TYPES.INITIALIZE_EDIT,
        payload: entry
      });
      
      setErrors([]);
      setLoading(false);
      setSubmitted(false);
      setSaveDisabled(false);
      setDesignNeedsReviewed(false);
      setInitialLoading(true);
    }
  }, [entry, open, dispatch]);

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
    setErrors([]);
    setSubmitted(false);
    setDesignNeedsReviewed(false);
    setSaveDisabled(false);
    setInitialLoading(false);
    setFormResetKey((key) => key + 1);
    dispatch({ type: ACTION_TYPES.RESET_STATE });
  };

  const handleReviewDesign = () => {
    const reviewDto = {
      clientDesignClients: entity.clientDesignClients.map((e) => e.id),
      name: entity.name,
      design: entity.design,
      provider: entity.provider,
    };
    
    homeDispatch({
      type: "NOTIFICATION",
      data: { message: "Reviewing Style Guide", loading: true }
    });
    
    api
      .post("/api/client-designs/review-clientDesign", reviewDto)
      .then((r) => {
        homeDispatch({
          type: "NOTIFICATION",
          data: {
            message: "Reviewing Style Guide success",
            type: "success"
          }
        });
        setDesignNeedsReviewed(false);
      })
      .catch((error) => errorManagement.formErrors(error, homeDispatch));
  };

  const handleCloseSave = () => {
    console.log("Current entity state before save:", {
      id: entity.id,
      name: entity.name, 
      design: entity.design,
      designLength: entity.design ? entity.design.length : 0,
      inputType: entity.inputType,
      provider: entity.provider,
      clientDesignClients: entity.clientDesignClients
    });
    
    if (!entity.design || entity.design.trim() === "") {
      homeDispatch({
        type: "NOTIFICATION",
        data: { message: "Style Guide content cannot be empty. Please ensure text is entered or a file is processed.", type: "error" }
      });
      return;
    }

    const updateDesign = {
      id: entity.id,
      clientDesignClients: entity.clientDesignClients.map((e) => e.id),
      name: entity.name,
      design: entity.design,
      inputType: entity.inputType,
      ocrOption: entity.ocrOption,
      fileId: entity.fileId,
      provider: entity.provider,
      designType: entity.designType,
      language: entity.language,
      productionStyleGuideId: entity.productionStyleGuideId,
      productionStyleGuideType: entity.productionStyleGuideType,
      productionStyleGuideMetadata: mapMetadataPayload(
        entity.productionStyleGuideMetadata
      ),
      priority: entity.priority ?? 1
    };

    homeDispatch({
      type: "NOTIFICATION",
      data: { message: "Updating " + entity.name, loading: true }
    });
    
    setSubmitted(true);
    setLoading(true);
    
    api
      .put(`/api/client-designs/${updateDesign.id}`, updateDesign)
      .then((response) => {
        homeDispatch({
          type: "NOTIFICATION",
          data: {
            message: entity.name + " updated",
            type: "success"
          }
        });
        setOpen(false);
        setLoading(false);
        setInitialLoading(false);
        setErrors([]);
        setSubmitted(false);
        setDesignNeedsReviewed(false);
        setSaveDisabled(false);
        setFormResetKey((key) => key + 1);
        dispatch({ type: ACTION_TYPES.RESET_STATE });
        onEdit();
      })
      .catch((error) => {
        setLoading(false);
        errorManagement.formErrors(error, homeDispatch, setErrors);
      });
  };

  const handleInitialLoadChange = useCallback(
    (isLoading) => setInitialLoading(!!isLoading),
    []
  );
  
  const actions = (
    <>
      <Button onClick={handleClose} disabled={loading}>
        Cancel
      </Button>
      {designNeedsReviewed === true ? (
        <Button
          onClick={handleReviewDesign}
          variant="outlined"
          color="primary"
          disabled={loading || initialLoading}
        >
          Review Style Guide
        </Button>
      ) : (
        <Button
          onClick={handleCloseSave}
          color="primary"
          variant="outlined"
          autoFocus
          disabled={loading || saveDisabled || initialLoading}
        >
          Save Style Guide
        </Button>
      )}
    </>
  );
  
  return (
    <GenericDialog
      maxWidth={"lg"}
      fullWidth
      open={open}
      onClose={handleClose}
      title={"Edit Style Guide"}
      actions={actions}
    >
        <Box sx={{ position: "relative", minHeight: 160 }}>
          {initialLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 160
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : null}
          <Box sx={{ display: initialLoading ? "none" : "block" }}>
            <Form
              key={formResetKey}
              mode={ClientDesignFormMode.EDIT}
              tags={tagsFromApi}
              engineFamilies={familiesFromApi}
              errors={errors}
              loading={loading}
              submitted={submitted}
              setSaveDisabled={setSaveDisabled}
              onInitialLoadChange={handleInitialLoadChange}
            />
          </Box>
        </Box>
    </GenericDialog>
  );
}

export default Edit;
